import { CheckCircleFilled } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Device } from "interface/device"
import { Visit } from "interface/visit"
import { useCallback, useMemo, useRef } from "react"
import { usePatientDevice } from "services/patients"
import { useRegisterDevice, useUnregisterDevice } from "services/visits"
import { useStepCompletion } from "./useStepCompletion"

export function StepUnlinkFootprint({ visit }: { visit: Visit }) {
	const device = usePatientDevice(visit.patient?.id!, ["status"])
	const register = useRegisterDevice(visit.id)
	const unregister = useUnregisterDevice(visit.id)
	const previousDevice = useRef<Device | undefined>(null)
	const { markStepAs, unlinkFootprint } = useStepCompletion(visit)
	const unlinkSuccess = unregister.isSuccess && unlinkFootprint
	const unlinkLabel = useMemo(() => {
		if (unlinkSuccess) {
			return "Re-link"
		}
		return "Unlink"
	}, [unlinkSuccess])
	const handleClick = useCallback(() => {
		if (!!previousDevice.current?.serialNumber) {
			register.mutate(previousDevice.current.serialNumber, {
				onSuccess() {
					previousDevice.current = null
					markStepAs("unlinkFootprint", false)
				},
			})
		} else if (!!device.data?.deviceId) {
			unregister.mutate(device.data.deviceId, {
				onSuccess() {
					previousDevice.current = device.data
					markStepAs("unlinkFootprint", true)
				},
			})
		}
	}, [device.data, markStepAs, register, unregister])

	return (
		<div className="flex size-full grow flex-col items-center justify-center gap-3 text-center">
			{unregister.isSuccess && unlinkFootprint && (
				<>
					<CheckCircleFilled
						aria-hidden
						className="mt-auto text-[48px] text-green-60"
					/>
					{`The Footprint with serial number ${previousDevice.current?.serialNumber} is unlinked`}
				</>
			)}

			{!unlinkFootprint && <>Unlink Footprint</>}

			{!!device.data?.deviceId && !device.isFetching && (
				<>
					<p className="text-navy-60">Serial number:</p>
					<p>{device.data.serialNumber}</p>
				</>
			)}

			<ErrorStatus
				className="mt-auto"
				show={
					!device.isFetching &&
					device.isSuccess &&
					!device.data?.deviceId &&
					!unlinkFootprint
				}
			>
				{`The patient ${visit.patient?.fullName} has no device linked`}
			</ErrorStatus>

			<ErrorStatus
				className="mt-auto"
				show={
					(unregister.isError && !unregister.isPending) ||
					(register.isError && !register.isPending) ||
					(device.isError && !device.isPending)
				}
			>
				{unregister.error?.message ||
					register.error?.message ||
					device.error?.message}
			</ErrorStatus>

			<Button
				type="primary"
				className="mt-auto w-full"
				disabled={
					device.isError ||
					(!previousDevice.current?.deviceId && !device.data?.deviceId)
				}
				loading={unregister.isPending || register.isPending}
				onClick={handleClick}
			>
				{unlinkLabel}
			</Button>
		</div>
	)
}
